
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Loader2, RefreshCw, AlertCircle, Shield } from "lucide-react";
import { useTranslation } from 'react-i18next'; 
import { login, sendLoginOtp } from "../../../services/auth.service";
import { EncryptionUtil } from "../../../utils/encryption.util";
import Cookies from "js-cookie";

export function LoginOTPVerification() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { email, displayEmail } = location.state || {};
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(300);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer((t) => t - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) {
      setError(t('otp.error'));
      return;
    }
    try {
      setLoading(true);
      const encryptedOtp = EncryptionUtil.encrypt(code);
      const res = await login(email, encryptedOtp);
      console.log("Login Response:", res);
      const user = res.data;
      
      
      const isLocalhost = window.location.hostname === 'localhost';

      const cookieOptions: Cookies.CookieAttributes = {
        expires: 7,
        path: '/',
        domain: isLocalhost ? undefined : '.morrocars.com',
        secure: !isLocalhost,
        sameSite: isLocalhost ? 'Lax' : 'None',
      };

      Cookies.set("token", user.token, cookieOptions);
      Cookies.set("customerType", String(user.customerType), cookieOptions);
      Cookies.set("user", JSON.stringify(user), cookieOptions);
      
      window.dispatchEvent(new Event('morrocars-auth-changed'));
      console.log('Stored user:', user);
      
      switch (user.customerType) {
        case 1:
          navigate("/");
          break;
        case 2:
          navigate("/dashboard");
          break;
        case 3:
          navigate("/dealer");
          break;
        default:
          navigate("/");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t('otp.error_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      const preferredLanguage = (i18n.resolvedLanguage || i18n.language || localStorage.getItem('selectedLanguage') || 'en').split(/[-_]/)[0];
      await sendLoginOtp(email, preferredLanguage);
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setResendTimer(300);
    } finally {
      setResending(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    if (value && index < 5) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pasted.split('').forEach((char, i) => { if (i < 6) newOtp[i] = char; });
    setOtp(newOtp);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB]">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-[#1E6DFF]" />
          </div>
          <h1 className="text-2xl text-[#2D323A] mb-2">{t('otp.title')}</h1>
          <p className="text-[#717182] text-sm">
            {t('otp.description')}<br />
            <span className="text-[#2D323A]">{displayEmail}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => { refs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className={`w-12 h-14 text-center text-xl bg-[#F5F7FA] border-2 rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors ${
                  error ? 'border-[#EF4444]' : digit ? 'border-[#1E6DFF]' : 'border-[#E5E7EB]'
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="flex items-center justify-center gap-1 text-[#EF4444] text-sm mb-4">
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || otp.join('').length < 6}
            className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {loading ? t('otp.button_loading') : t('otp.button')}
          </button>
        </form>

        <div className="text-center mt-6">
          {resendTimer > 0 ? (
            <p className="text-sm text-[#717182]">
              {t('otp.timer')}{" "}
              <span className="text-[#1E6DFF]">
                {Math.floor(resendTimer / 60)}:{(resendTimer % 60).toString().padStart(2, "0")}
              </span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              className="flex items-center gap-1 text-sm text-[#1E6DFF] hover:underline mx-auto disabled:opacity-60"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {resending ? t('otp.resending') : t('otp.resend')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}